import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { type PracticeOption, usePracticeContext } from "@/hooks/usePracticeContext";

/** Top-right practice switcher for DoctorUser sessions (PRD-010 Stage 2 §Phase 2.2).
 *
 *  Shows the user's full set of switcher options: configured hlas-emmy practices first, then any Emmy facilities
 *  (from `cognito:groups`) that have no matching practice row. Picking an unconfigured entry navigates to
 *  `/facility-not-set-up/:facilityId` so the user sees the "contact support" page rather than a blank settings screen.
 *
 *  Rendering rules keyed on the total option count:
 *    - 0 options → not rendered (the app renders `<NoAccessPage />` instead).
 *    - 1 option  → static label (no dropdown affordance — there's nothing to choose).
 *    - 2+        → button + popover with a check next to the active entry; unconfigured entries are italicised to
 *                  hint at their "not yet set up" status.
 */
export const PracticeCombobox = () => {
  const { options, activeOption, setActiveOption } = usePracticeContext();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!open) return;
    const onDocumentClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocumentClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocumentClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (options.length === 0 || !activeOption) return null;

  const activeLabel = labelFor(activeOption);

  if (options.length === 1) {
    return (
      <span
        className={
          "text-sm font-medium text-white/80 " + (activeOption.kind === "unconfigured" ? "italic" : "")
        }
      >
        {activeLabel}
      </span>
    );
  }

  const handleSelect = (opt: PracticeOption) => {
    setActiveOption(opt);
    setOpen(false);
    // Navigation policy: if the user is on a practice-scoped subroute (e.g. `/practices/X/messages`,
    // `/practices/X/hours`), preserve the SAME tab when switching practices — swap X for Y. If they're
    // on a top-level practice-agnostic route (`/calls`, `/calls/:uuid`), stay there. Picking an
    // unconfigured facility forces the contact-support page since none of the practice routes apply.
    if (opt.kind === "unconfigured") {
      navigate(`/facility-not-set-up/${opt.facilityId}`);
      return;
    }
    const newPracticeId = opt.practice.id;
    const path = location.pathname;
    const practiceMatch = path.match(/^\/practices\/[^/]+(\/.*)?$/);
    if (practiceMatch) {
      // Preserve sub-path (everything after `/practices/{id}`).
      const subPath = practiceMatch[1] ?? "";
      navigate(`/practices/${newPracticeId}${subPath}`);
    }
    // Else: stay on the current top-level route (e.g. /calls). The pages that depend on the active
    // practice (Calls list) re-derive from `usePracticeContext().activePractice` so they refresh
    // automatically when `setActiveOption` changes the context value above.
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 rounded-md px-2.5 py-1.5 text-sm text-white transition-colors hover:bg-white/10"
      >
        <span className={"font-medium " + (activeOption.kind === "unconfigured" ? "italic" : "")}>{activeLabel}</span>
        <svg className="h-4 w-4 opacity-70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-10 z-50 min-w-[16rem] rounded-lg border border-gray-200 bg-white p-1.5 shadow-lg"
        >
          {options.map((opt) => {
            const selected = isSameOption(opt, activeOption);
            const label = labelFor(opt);
            return (
              <button
                key={keyFor(opt)}
                type="button"
                role="menuitem"
                onClick={() => handleSelect(opt)}
                className={
                  "flex w-full items-start justify-between gap-3 rounded-md px-3 py-2 text-left text-sm text-gray-900 hover:bg-gray-100 " +
                  (selected ? "font-semibold" : "")
                }
              >
                <span className="flex flex-col">
                  <span className={"truncate " + (opt.kind === "unconfigured" ? "italic" : "")}>{label}</span>
                  {opt.kind === "unconfigured" && (
                    <span className="text-xs font-normal text-gray-500">hlas emmy zatím nenastaven</span>
                  )}
                </span>
                {selected && (
                  <svg className="h-4 w-4 shrink-0 text-[#1a2b6b]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const labelFor = (opt: PracticeOption): string => {
  if (opt.kind === "configured") return opt.practice.displayName;
  return opt.name && opt.name.length > 0 ? opt.name : `Ordinace ${opt.facilityId.slice(0, 8)}`;
};

const keyFor = (opt: PracticeOption): string =>
  opt.kind === "configured" ? `p:${opt.practice.id}` : `f:${opt.facilityId}`;

const isSameOption = (a: PracticeOption, b: PracticeOption): boolean => {
  if (a.kind !== b.kind) return false;
  if (a.kind === "configured" && b.kind === "configured") return a.practice.id === b.practice.id;
  if (a.kind === "unconfigured" && b.kind === "unconfigured") return a.facilityId === b.facilityId;
  return false;
};
